import type {DiffAnchor} from '@github-ui/diffs/types'
import {useAnalytics} from '@github-ui/use-analytics'
import {type FocusEvent, useMemo, useRef} from 'react'

import type {StartThreadCommentProps} from './StartThreadComment'
import {StartThreadComment} from './StartThreadComment'
import {useGetSelectedLineRange, useTreeEntries} from '../../stores/selectors'
import type {DiffLine} from '../../types'
import {useSuggestedChanges} from '../../hooks/use-suggested-changes'
import {useStartConversationInfo} from '../../hooks/use-start-conversation-info'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useCreateReviewCommentMutation} from '@github-ui/pull-requests/use-create-review-comment-mutation'
import {ReviewCommentFactory} from '../../helpers/review-comment-factory'
import {useComparisonInfo, useSelectedRange} from '@github-ui/pull-requests/pull-request-store-selectors'
import {useCommentDialogTitle} from '../../hooks/use-comment-dialog-title'

export interface StartConversationProps extends Pick<
  StartThreadCommentProps,
  'batchingEnabled' | 'fileLevelComment' | 'filePath' | 'focusOnMount' | 'viewerData' | 'threadsConnectionId'
> {
  diffAnchor: DiffAnchor
  diffLine: DiffLine
  isInDialogMode: boolean
  isLeftSide: boolean
  isOpen: boolean
  isRowSelected: boolean
  onCloseCommentDialog: (shouldFocusCell?: boolean) => void
  onCommentCreated?: () => void
  shouldStartNewConversationWithSuggestedChange?: boolean
}

/**
 * Renders a modal AnchoredOverlay for starting a new conversation thread or an in line version
 */
export function StartConversation({
  diffAnchor,
  isInDialogMode,
  isLeftSide,
  isOpen,
  isRowSelected,
  onCloseCommentDialog,
  onCommentCreated,
  diffLine,
  shouldStartNewConversationWithSuggestedChange,
  ...rest
}: StartConversationProps) {
  const {sendAnalyticsEvent} = useAnalytics()
  const newCommentContainerRef = useRef<HTMLDivElement>(null)
  const getSelectedLineRange = useGetSelectedLineRange()

  const startConversationInfo = useStartConversationInfo({
    line: diffLine,
    isLeftSide,
    isRowSelected,
  })

  const suggestedChangesConfig = useSuggestedChanges(diffLine, shouldStartNewConversationWithSuggestedChange)
  const {newTreeEntry, oldTreeEntry} = useTreeEntries()
  const {effectiveRange: effectiveDiffRange} = useComparisonInfo()
  const {baseOid: startOid, headOid: endOid} = useSelectedRange()
  const commentOutsideOfTheDiffEnabled = useFeatureFlag('cotd_ui')
  const addCommentDialogTitle = useCommentDialogTitle(startConversationInfo)

  const {mutate: createReviewCommentMutation} = useCreateReviewCommentMutation()
  const handleAddComment = ({
    onCompleted,
    onError,
    submitBatch,
    text,
  }: {
    onCompleted?: (threadId: string, commentDatabaseId: number | undefined | null) => void
    onError: (error: Error) => void
    submitBatch?: boolean | undefined
    text: string
  }) => {
    if (!diffLine) return

    if (commentOutsideOfTheDiffEnabled) {
      const reviewCommentFactory = new ReviewCommentFactory({
        filePath: rest.filePath,
        newTreeEntry,
        oldTreeEntry,
        effectiveDiffRange,
        submitBatch,
        text,
      })

      const requestData = startConversationInfo.isMultiline
        ? reviewCommentFactory.multilineRequestData({
            startLineNumber: startConversationInfo.startLineNumber as number,
            startSide: startConversationInfo.startSide as 'left' | 'right',
            endLineNumber: startConversationInfo.lineNumber,
            endSide: startConversationInfo.side,
          })
        : reviewCommentFactory.lineRequestData({
            lineNumber: startConversationInfo.lineNumber,
            side: startConversationInfo.side,
          })

      createReviewCommentMutation(requestData, {
        onError,
        onSuccess: data => {
          onCompleted?.(data.thread.id, data.comment?.databaseId)
          onCommentCreated?.()
        },
      })
    } else {
      createReviewCommentMutation(
        {
          text,
          line: startConversationInfo.lineNumber,
          path: rest.filePath,
          side: startConversationInfo.side,
          startSide: startConversationInfo.startSide,
          submitBatch,
          startLine: startConversationInfo.startLineNumber,
          comparisonEndOid: endOid,
          comparisonStartOid: startOid,
        },
        {
          onError,
          onSuccess: data => {
            onCompleted?.(data.thread.id, data.comment?.databaseId)
            onCommentCreated?.()
          },
        },
      )
    }
  }

  const config = useMemo(() => {
    if (isOpen && suggestedChangesConfig?.configureSuggestedChangesFromLineRange) {
      const selectedLineRange = getSelectedLineRange()
      return suggestedChangesConfig.configureSuggestedChangesFromLineRange(
        selectedLineRange,
        suggestedChangesConfig?.shouldStartNewConversationWithSuggestedChange,
      )
    }
    return undefined
  }, [isOpen, suggestedChangesConfig, getSelectedLineRange])

  if (!isOpen) return null

  const handleNewCommentContainerFocus = (e: FocusEvent) => {
    // Only handle focus logic if a user is on the new comment container's first <div> element.
    if (e.target !== newCommentContainerRef.current) return

    // Focus on the textarea element inside of the new comment container
    newCommentContainerRef.current.querySelector('textarea')?.focus()
  }

  return (
    <div
      className="rounded-2 bgColor-default"
      data-marker-navigation-new-thread
      data-marker-id="new-comment" // Add data-marker-id attribute for keyboard navigation
      ref={newCommentContainerRef}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={isInDialogMode ? 0 : -1}
      onFocus={handleNewCommentContainerFocus}
    >
      <StartThreadComment
        onClose={(shouldFocusCell?: boolean) => {
          onCloseCommentDialog(shouldFocusCell)
          sendAnalyticsEvent('comments.cancel_thread_reply', 'CANCEL_REVIEW_THREAD_BUTTON')
        }}
        showOnCloseIcon={false}
        suggestedChangesConfig={config}
        onAddComment={handleAddComment}
        addCommentDialogTitle={addCommentDialogTitle}
        lineNumber={startConversationInfo.lineNumber}
        startLineNumber={startConversationInfo.startLineNumber}
        isLeftSide={startConversationInfo.side === 'left'}
        {...rest}
      />
    </div>
  )
}

try{ StartConversation.displayName ||= 'StartConversation' } catch {}